import { QueryClient, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import { calendarKeys } from "./calendar.keys";
import { appointmentKeys } from "../Appointment/appointment.keys";
import {
  AppointmentDto,
  LeaveOfAbsenceDto,
  UpdateAppointmentDto,
  WorkHourExceptionDto,
} from "../../api-client";
import notificationStore from "../Notification/notificationStore";
import {
  CalendarCacheSnapshot,
  applyCalendarCachePatch,
  beginAppointmentMutation,
  isLatestAppointmentMutation,
  optimisticAppointmentUpsert,
  optimisticCalendarPatch,
  optimisticSlotSchedule,
  optimisticTimeSlotPatch,
  reconcileSurvivorAppointment,
  restoreCalendarCaches,
  restoreShelfCaches,
  sectionForTimeSlotType,
  ContinuousCacheSnapshot,
  WorkingHoursCacheSnapshot,
  BreakMatch,
  optimisticContinuousTimeSlotPatch,
  optimisticContinuousEventTimeSlotPatch,
  optimisticContinuousEventTimeSlotDelete,
  optimisticTimeSlotDelete,
  restoreContinuousCaches,
  optimisticBreakPatch,
  restoreWorkingHoursCaches,
} from "./AppointmentsOfAllTypes.utils";
import { useLocationId } from "../User/User.hooks";
import moment from "moment";
// ⚠️ TEMPORARY DIAGNOSTIC — REMOVE before merging (see dragTrace.ts).
// import { traceMutationFired } from "../../molecules/Calendar/dragTrace";

/**
 * NOTE: Global error/success handling is configured in src/stores/queryClient.ts
 * - MutationCache handles automatic error notifications via notificationStore
 * - MutationCache handles automatic success notifications via meta.successMessage
 * - No need to manually call notificationStore in individual mutations
 */

/**
 * Mutation to schedule an otherwise unscheduled appointment
 * Replaces CalendarStore.saveAppointment
 */
export const useSaveAppointment = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
 const locationId = useLocationId();

  return useMutation({
    mutationFn: async ({
      id,
      appointment,
    }: {
      id: string;
      appointment: AppointmentDto;
      // Optional fully-hydrated source appointment (patient + relations) used ONLY
      // for the optimistic cache insert when scheduling from the shelf. Never sent
      // to the API — the lean `appointment` payload is the reschedule body.
      optimisticAppointment?: AppointmentDto;
      // Optional hook invoked synchronously right AFTER the optimistic cache patch
      // is written in onMutate. Callers use it to apply dependent client-side state
      // (e.g. un-pinning the placed "Ablage" item) in the SAME render cycle as the
      // patch, so visibility filters keyed on that state release atomically with
      // the optimistic placement instead of after the server roundtrip.
      onOptimisticApplied?: () => void;
    }) => {
      // ⚠️ TEMPORARY DIAGNOSTIC — REMOVE before merging (see dragTrace.ts).
      // traceMutationFired(String(id));
      // The unified scheduler RESCHEDULE verb. Returns the canonical survivor
      // appointment DTO (the deprecated calendar.updateAppointment used to return
      // a richer { affectedPatientId, updatedAppointment } envelope).
      return await scheduler.schedulerReschedule(
        id,
        appointment as unknown as UpdateAppointmentDto,
        undefined,
        false
      );
    },
    onMutate: async ({ id, appointment, optimisticAppointment, onOptimisticApplied }) => {
      // Provisional cache upsert so the cache agrees with FullCalendar's optimistic
      // placement while the request is in flight: merge for moves/resizes of cached
      // appointments, insert for planning from the shelf. Any rebuild of the
      // controlled events array during this window can therefore no longer snap
      // the event back to its old position. The canonical server DTO replaces
      // this in onSuccess.
      //
      // For shelf inserts the lean `appointment` payload has no patient, so when the
      // caller supplies the fully-hydrated source appointment we pass it as the
      // insert base — the new placement is merged onto it, keeping the optimistic
      // event render-complete (no patient-less flash) until onSuccess.
      //
      // Stamp this move with a per-appointment sequence so that if a SECOND move
      // of the same appointment is issued before this one settles, this (now
      // stale) request's onSuccess/onError can no longer clobber the newer
      // placement when it completes out of order.
      const seq = beginAppointmentMutation(id);
      const snapshot: CalendarCacheSnapshot = await optimisticAppointmentUpsert(
        queryClient,
        { ...appointment, id },
        optimisticAppointment ? { ...optimisticAppointment, id } : undefined
      );
      // Both the cache write above and any state change made by this callback land
      // in the same microtask, so React commits them as ONE frame.
      onOptimisticApplied?.();
      return { snapshot, seq };
    },
    onError: (_error, variables, context) => {
      // Only the latest issued move for this appointment owns the rollback; a
      // stale failing request must not restore a snapshot that predates a newer,
      // successful move (which would drag other appointments back too).
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      // Roll back the provisional patch — FullCalendar reverts via the rebuild.
      if (context?.snapshot) {
        restoreCalendarCaches(queryClient, context.snapshot);
      }
    },
    onSuccess: async (data: any, variables, context) => {
      const { appointment } = variables;
      const updatedAppointment: any = data;

      // Out-of-order guard: if a newer move of this same appointment has since
      // been issued, this response is stale. Skip all cache writes/invalidations
      // so the appointment does not snap back to this (older) server position —
      // the newest move's onSuccess will set the canonical state.
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }

      // Derive the affected patient from the returned appointment's slots
      // (the deprecated endpoint returned affectedPatientId directly).
      // Sales slots carry the patient directly on the slot instead of via
      // therapyRx, so fall back to slot.patient.
      let patientId: any = (updatedAppointment?.timeSlots ?? [])
        .map((slot: any) => slot?.therapyRx?.rx?.patient?.id ?? slot?.patient?.id)
        .find(Boolean);

      if (typeof patientId === "string") {
        patientId = parseInt(patientId);
      }
      // Detect group appointments from both the sent appointment and the API response.
      // When a *patient* is added to a group, the saved appointment carries
      // GroupTimeSlotPatient (not the therapist slot) and/or a groupAppointmentId.
      const isGroupTimeSlot = (ts: { type?: string; groupAppointmentId?: string }) =>
        ts.type === "GroupTimeSlotTherapist" ||
        ts.type === "ContinuousGroupTimeSlotTherapist" ||
        ts.type === "GroupTimeSlotPatient" ||
        ts.type === "ContinuousGroupTimeSlotPatient" ||
        !!ts.groupAppointmentId;

      const isGroupAppointment =
        appointment.timeSlots?.some(isGroupTimeSlot) ||
        updatedAppointment?.timeSlots?.some(isGroupTimeSlot);

      if (isGroupAppointment) {
        // Try to extract patient info from the appointment to invalidate specific queries
        // For now, invalidate broader queries to ensure data consistency

        await queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.all(),
        });

        // Invalidate unscheduled appointments queries
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.all(),
        });
        // The "Offene Termine" shelf combines unscheduled AND cancelled
        // appointments, so a rescheduled item that originated as a cancellation
        // would otherwise linger on the shelf until a manual reload.
        await queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.all(),
        });
        await queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        });

        // Group moves have multi-appointment side effects (anchor participants,
        // vacated anchors) that the survivor DTO alone cannot cover. The refetch
        // is a background confirm: the cache already holds the optimistic patch,
        // so no snap-back can occur while it is in flight.
        const eventDate = moment(updatedAppointment.start!).toDate();
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.appointmentsForDate.detail(eventDate),
        });
        
        // sales appointments
        await queryClient.invalidateQueries({
          queryKey: appointmentKeys.serviceAppointmentsByPatient.all(),
        });
      } else if (patientId) {
        // Try to extract patient info from the appointment to invalidate specific queries
        // For now, invalidate broader queries to ensure data consistency

        await queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(patientId),
        });

        // Invalidate unscheduled appointments queries
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.detail(patientId),
        });
        // The "Offene Termine" shelf combines unscheduled AND cancelled
        // appointments, so a rescheduled item that originated as a cancellation
        // would otherwise linger on the shelf until a manual reload.
        await queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(patientId),
        });

        // sales appointments
        await queryClient.invalidateQueries({
          queryKey: appointmentKeys.serviceAppointmentsByPatient.detail(patientId),
        });

      } else {
        notificationStore.showMessage(
          "UNABLE TO extract patient ID from scheduled appointment when trying to invalidate queries in mutation",
          "warning",
        );
        // console.debug("appointment timeSlots:", appointment.timeSlots, appointment);
      }

      // Rescheduling a cancellation resolves it server-side, which the survivor DTO
      // alone cannot express — the day query feeding the "Stornierungen" view has to
      // be refetched or the appointment keeps rendering as cancelled.
      const wasCancellation = !!updatedAppointment?.originalDate;

      // Replace the provisional onMutate patch with the canonical survivor DTO
      // (calendar-shaped, including group participants) — no day refetch needed.
      if (updatedAppointment) {
        applyCalendarCachePatch(queryClient, "appointments", "Update", updatedAppointment);
      }

      if (!isGroupAppointment && wasCancellation) {
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.appointmentsForDate.detail(
            moment(updatedAppointment.start!).toDate(),
          ),
        });
      }

      // Invalidate appointments by RX if available
      // Gather all RX IDs from timeSlots
      const rxIds: any = [
        ...new Set(
          updatedAppointment.timeSlots?.map((slot: any) => slot.therapyRx?.rx?.id).filter(Boolean),
        ),
      ];

       if (rxIds && rxIds.length > 0 && !isGroupAppointment) {
        // Invalidate all RX-based queries
        for (const rxId of rxIds) {
          await queryClient.invalidateQueries({
            queryKey: appointmentKeys.appointmentsByRx.detail(rxId),
          });
        }
      } else {
        const onlyServiceSlots = appointment.timeSlots?.every(
          (slot) => slot.type === "SalesTimeSlot"
        );
        if (!onlyServiceSlots   && !isGroupAppointment) {
          notificationStore.showMessage(
            "UNABLE TO extract RX ID from scheduled appointment when trying to invalidate queries in mutation",
            "warning"
          );
          // console.debug("appointment timeSlots:", appointment.timeSlots);
        }
      }

      // Date-range cache (single therapist view): refetch so the rescheduled
      // appointment shows up immediately. Only active queries are refetched,
      // so this is a no-op in the normal day view.
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.appointmentsForDateRange.all(),
      });

      // TODO modify the cache for appointmentKeys.unscheduledAppointments to remove this item
    },
    meta: {
      successMessage: "Termin erfolgreich gespeichert",
      errorMessage: "Fehler beim Speichern des Termins",
    },
  });
};
export const useDeleteGroupTherapist = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      // Cleanup-only: purge an EMPTY group-therapist anchor (refuses if participants remain).
      return await scheduler.schedulerPurgeEmptyGroupAnchor(id);
    },
    meta: {
      successMessage: "Gruppentermin erfolgreich gelöscht", 
      errorMessage: "Fehler beim Löschen des Gruppentermins",
    },
  });
}

/**
 * Mutation to delete a (concrete) timeslot.
 * Replaces CalendarStore.deleteTimeslot — funnels through the central scheduler
 * (deletes the parent appointment if it becomes empty).
 */
export const useDeleteTimeSlot = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (timeSlotId: string) => {
      try {
        return await scheduler.schedulerDeleteTimeSlot(timeSlotId, false);
      } catch (err) {
        // A 404 means the slot was already deleted — the desired end state. Treat it
        // as an idempotent success so the optimistic removal stays and no misleading
        // "not found" error is surfaced to the user.
        if ((err as any)?.response?.status === 404) return;
        throw err;
      }
    },
    onMutate: async (timeSlotId: string) => {
      // Remove the slot from the calendar cache immediately so the controlled events
      // array cannot rebuild the deleted entry back onto the calendar while the
      // request is in flight (FullCalendar's own event.remove() is only transient).
      const snapshot = await optimisticTimeSlotDelete(queryClient, timeSlotId);
      return { snapshot };
    },
    onError: (_error, _timeSlotId, context) => {
      if (context?.snapshot) {
        restoreCalendarCaches(queryClient, context.snapshot);
      }
    },
    meta: {
      errorMessage: "Fehler beim Löschen des Zeitfensters",
    },
  });
};

/**
 * Mutation to delete a continuous timeslot.
 * Replaces CalendarStore.deleteContinuousTimeslot — funnels through the central scheduler.
 */
export const useDeleteContinuousTimeSlot = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (timeSlotId: string) => {
      try {
        return await scheduler.schedulerDeleteTimeSlot(timeSlotId, true);
      } catch (err) {
        // A 404 means the slot was already deleted — treat as an idempotent success.
        if ((err as any)?.response?.status === 404) return;
        throw err;
      }
    },
    onMutate: async (timeSlotId: string) => {
      // Remove the slot from the continuous caches immediately (the continuous delete
      // emits no websocket echo), so the deleted entry isn't rebuilt back onto the
      // calendar until a manual reload.
      const snapshot = await optimisticContinuousEventTimeSlotDelete(queryClient, timeSlotId);
      return { snapshot };
    },
    onError: (_error, _timeSlotId, context) => {
      if (context?.snapshot) {
        restoreContinuousCaches(queryClient, context.snapshot);
      }
    },
    meta: {
      errorMessage: "Fehler beim Löschen des kontinuierlichen Zeitfensters",
    },
  });
};

/**
 * Mutation to update a continuous timeslot
 * Replaces CalendarStore.updateContinuousTimeSlot
 */
export const useUpdateContinuousTimeSlot = () => {
  const { calendar: calendarApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, timeslot }: { id: string; timeslot: any }) => {
      // ⚠️ TEMPORARY DIAGNOSTIC — REMOVE before merging (see dragTrace.ts).
      // EventTimeSlot/TravelTime moves route here, not through useSaveAppointment,
      // so without this the tracer false-flags them as "LOST MOVE".
      // traceMutationFired(String(id));
      return await calendarApi.updateContinuousTimeSlot(id, timeslot);
    },
    onMutate: async ({ id, timeslot }) => {
      // Provisional patch of the continuous event timeslot's window/user across
      // both continuous caches so the continuous calendar agrees with
      // FullCalendar's optimistic placement during the in-flight window — a
      // rebuild of the controlled events array can no longer snap it back.
      //
      // Stamp this move with a per-slot sequence so that if a SECOND move of the
      // same slot is issued before this one settles, this (now stale) request's
      // onSuccess/onError can no longer clobber the newer placement when it
      // completes out of order (last-issued-wins).
      const seq = beginAppointmentMutation(id);
      const snapshot: ContinuousCacheSnapshot = await optimisticContinuousEventTimeSlotPatch(
        queryClient,
        id,
        { start: timeslot?.start, end: timeslot?.end, userId: timeslot?.user?.id }
      );
      return { snapshot, seq };
    },
    onError: (_error, variables, context) => {
      // Only the latest issued move for this slot owns the rollback; a stale
      // failing request must not restore a snapshot that predates a newer move.
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      if (context?.snapshot) {
        restoreContinuousCaches(queryClient, context.snapshot);
      }
    },
    onSuccess: (_data, variables, context) => {
      // Out-of-order guard: skip invalidations if a newer move of this same slot
      // has since been issued, so it does not snap back to this older position.
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      // Invalidate continuous appointments cache
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousAppointmentsByDay.all(),
      });
      queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
      });
    },
    meta: {
      successMessage: "Dauertermin erfolgreich geändert",
      errorMessage: "Fehler beim Ändern des Dauertermins",
    },
  });
};

/**
 * Mutation to update a regular timeslot
 * Replaces CalendarStore.updateTimeSlot
 */
export const useUpdateTimeSlot = () => {
  const { calendar: calendarApi } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, timeslot }: { id: string; timeslot: any }) => {
      // ⚠️ TEMPORARY DIAGNOSTIC — REMOVE before merging (see dragTrace.ts).
      // EventTimeSlot/TravelTime moves route here, not through useSaveAppointment,
      // so without this the tracer false-flags them as "LOST MOVE".
      // traceMutationFired(String(id));
      return await calendarApi.updateTimeSlot(id, timeslot);
    },
    onMutate: async ({ id, timeslot }) => {
      // Provisional window/user patch in the matching DTO section so the cache
      // agrees with FullCalendar's optimistic placement during the in-flight window.
      //
      // Stamp this move with a per-slot sequence so that if a SECOND move of the
      // same slot is issued before this one settles, this (now stale) request's
      // onSuccess/onError can no longer clobber the newer placement when it
      // completes out of order (last-issued-wins).
      const seq = beginAppointmentMutation(id);
      const section = sectionForTimeSlotType(timeslot?.type);
      if (!section) return { snapshot: undefined, seq };
      const snapshot = await optimisticCalendarPatch(queryClient, section, id, (cached: any) => ({
        ...cached,
        start: timeslot.start ?? cached.start,
        end: timeslot.end ?? cached.end,
        user: timeslot.user?.id ? { ...cached.user, ...timeslot.user } : cached.user,
      }));
      return { snapshot, seq };
    },
    onError: (_error, variables, context) => {
      // Only the latest issued move for this slot owns the rollback; a stale
      // failing request must not restore a snapshot that predates a newer move.
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      if (context?.snapshot) {
        restoreCalendarCaches(queryClient, context.snapshot);
      }
    },
    onSuccess: (data: any, variables, context) => {
      // Out-of-order guard: if a newer move of this same slot has since been
      // issued, this response is stale. Skip the cache write so the slot does not
      // snap back to this (older) server position — the newest move owns it.
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      // Merge the canonical server timeslot over the provisional patch — the
      // upsert merges fields, so relations missing from the generic TimeSlotDto
      // response (e.g. TravelTime from/to) are preserved from the cache.
      const section = sectionForTimeSlotType(variables.timeslot?.type ?? data?.type);
      if (section && data?.id) {
        applyCalendarCachePatch(queryClient, section, "Update", data);
      }
    },
    meta: {
      successMessage: "Termin erfolgreich geändert",
      errorMessage: "Fehler beim Ändern des Termins",
    },
  });
};


export const useCreateWorkHourException = () => {
  const { workingHours: api } = useApi();
  const queryClient = useQueryClient();
  const locationId = useLocationId();

  return useMutation({
    mutationFn: async ({ exception }: { exception: WorkHourExceptionDto }) => {
      // MD-286: a day-level hour change belongs to the site it was edited at —
      // the backend's cancel/restore sweep only touches that site's slots.
      const stamped =
        (exception as any).locationId == null && locationId
          ? { ...exception, locationId }
          : exception;
      return await api.createWorkHourException(stamped as WorkHourExceptionDto);
    },
    onMutate: async ({ exception }) => {
      // Provisional patch of the break's window across the workingHours caches so
      // the break does not snap back while the exception is created in flight.
      // A brand-new exception has no slot id yet, so match by user + day.
      const slot = exception.slots?.[0];
      if (!slot?.isBreak || !slot.start || !slot.end) return { snapshot: undefined };
      const match: BreakMatch = {
        slotId: slot.slotId,
        userId: exception.userId,
        date: exception.date,
      };
      const snapshot: WorkingHoursCacheSnapshot = await optimisticBreakPatch(
        queryClient,
        match,
        { start: slot.start, end: slot.end }
      );
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        restoreWorkingHoursCaches(queryClient, context.snapshot);
      }
    },
    onSuccess: async (data, variables) => {
      const { exception } = variables;
      
      // Invalidate derived working hours cache
      if (exception.date) {
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.workingHours.all(),
        });
        // Invalidate delta summary to update over/under hours badges
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.deltaSummary.all(),
          });
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.continuousWorkPlansForUsers.all(),
        });
      }
    },
    meta: {
      successMessage: "Arbeitsplan erfolgreich geändert",
      errorMessage: "Fehler beim Ändern des Arbeitsplans",
    },
  });
};

export const useUpdateWorkHourException = () => {
  const { workingHours: api } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({exceptionId, exception}: {exceptionId:string, exception: WorkHourExceptionDto }) => {
      return await api.updateWorkHourException(exceptionId,exception);
    },
    onMutate: async ({ exception }) => {
      // Provisional patch of the break's window across the workingHours caches so
      // the break does not snap back while the exception update is in flight. An
      // existing exception carries the original slot id, so match by slot id.
      const slot = exception.slots?.[0];
      if (!slot?.isBreak || !slot.start || !slot.end) return { snapshot: undefined };
      const match: BreakMatch = {
        slotId: slot.slotId,
        userId: exception.userId,
        date: exception.date,
      };
      const snapshot: WorkingHoursCacheSnapshot = await optimisticBreakPatch(
        queryClient,
        match,
        { start: slot.start, end: slot.end }
      );
      return { snapshot };
    },
    onError: (_error, _variables, context) => {
      if (context?.snapshot) {
        restoreWorkingHoursCaches(queryClient, context.snapshot);
      }
    },
    onSuccess: async (data, variables) => {
      const { exception } = variables;
      
      // Invalidate derived working hours cache
      if (exception.date) {
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.workingHours.all(),
        });
        // Invalidate delta summary to update over/under hours badges
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.deltaSummary.all(),
        });
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.continuousWorkPlansForUsers.all(),
        });
      }
    },
    meta: {
      successMessage: "Arbeitsplan erfolgreich geändert",
      errorMessage: "Fehler beim Ändern des Arbeitsplans",
    },
  });
};

export const useDeleteWorkHourException = () => {
  const { workingHours: api } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({exceptionId}: {exceptionId:string}) => {
      return await api.deleteException(exceptionId);
    },
    onSuccess: async (data, variables) => {
      // Invalidate appointments cache to show restored appointments
      // Only invalidate the specific date that was affected
      if (data.affectedDate) {
        const affectedDate = new Date(data.affectedDate);
        queryClient.invalidateQueries({
          queryKey: calendarKeys.appointmentsForDate.detail(affectedDate),
        });
      }
      
      // Invalidate working hours cache
      queryClient.invalidateQueries({
        queryKey: calendarKeys.workingHours.all(),
      });
      
      // Invalidate delta summary to update over/under hours badges
      queryClient.invalidateQueries({
        queryKey: calendarKeys.deltaSummary.all(),
      });
      
      // Invalidate unscheduled queries - appointments may have been restored
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledPatientAppointments.all(),
      });
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
      });
      // Invalidate cancelled appointments query (appointments restored from cancellation)
      await queryClient.invalidateQueries({
        queryKey: appointmentKeys.cancelledAppointmentsByPatient.all(),
      });
      // Invalidate main "Offene Termine" query
      await queryClient.invalidateQueries({
        queryKey: [appointmentKeys.unscheduledAppointments.scope],
      });
    },
    meta: {
      successMessage: "Ausnahme erfolgreich entfernt - Zurück zum ursprünglichen Arbeitsplan",
      errorMessage: "Fehler beim Entfernen der Ausnahme",
    },
  });
};

export const useCancelDay = () => {
  const { workingHours: api } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, date }: { userId: string; date: string }) => {
      return await api.cancelDay({ userId, date });
    },
    onSuccess: async (data, variables) => {
      const affectedDate = new Date(variables.date);
      
      // Invalidate working hours and appointments to refresh the view
      queryClient.invalidateQueries({
        queryKey: calendarKeys.workingHours.all(),
      });
      // Only invalidate the specific date that was cancelled
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointmentsForDate.detail(affectedDate),
      });
      // Invalidate unscheduled queries so cancelled appointments appear in "Offene Termine"
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledPatientAppointments.all(),
      });
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
      });
      // Invalidate cancelled appointments query (EventShelf uses both unscheduled AND cancelled)
      await queryClient.invalidateQueries({
        queryKey: appointmentKeys.cancelledAppointmentsByPatient.all(),
      });
      // Invalidate main "Offene Termine" query (location-wide unscheduled appointments)
      await queryClient.invalidateQueries({
        queryKey: [appointmentKeys.unscheduledAppointments.scope],
      });
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousWorkPlansForUsers.all(),
      });
    },
    meta: {
      successMessage: "Tag erfolgreich abgesagt.",
      errorMessage: "Fehler beim Absagen des Tages",
    },
  });
};

/**
 * Mutation to extend working hours to accommodate an appointment
 * Used when scheduling appointments outside current working hours
 */
export const useExtendWorkingHoursForAppointment = () => {
  const { workingHours: api } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: {
      userId: string;
      date: string;
      appointmentStart: Date;
      appointmentEnd: Date;
    }) => {
      return await api.extendWorkingHoursForAppointment({
        userId: dto.userId,
        date: dto.date,
        appointmentStart: dto.appointmentStart.toISOString(),
        appointmentEnd: dto.appointmentEnd.toISOString(),
      });
    },
    onSuccess: async () => {
      // Invalidate working hours cache to refresh calendar
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.workingHours.all(),
      });
      // Invalidate delta summary to update over/under hours badges
      await queryClient.invalidateQueries({
        queryKey: calendarKeys.deltaSummary.all(),
      });
    },
    meta: {
      successMessage: "Arbeitszeiten erfolgreich erweitert",
      errorMessage: "Fehler beim Erweitern der Arbeitszeiten",
    },
  });
};

/**
 * Mutation to delete a lunch break for a specific day.
 * With `slotId`, only that specific break is removed (other breaks keep
 * their identity — MD-238 P3); without it, all breaks of the day are removed.
 * The backend blocks the deletion (400) when the resulting day would violate
 * § 4 ArbZG — the error message is surfaced to the user.
 */
export const useDeleteLunchBreakForDay = () => {
  const { workingHours: api } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, date, slotId }: { userId: string; date: string; slotId?: string }) => {
      return await api.deleteLunchBreakForDay(userId, date, slotId);
    },
    onSuccess: (data, variables) => {
      // Invalidate working hours to refresh the view
      queryClient.invalidateQueries({
        queryKey: calendarKeys.workingHours.all(),
      });
    },
    onError: () => {
      // Blocked delete (§ 4 ArbZG): the calendar removed the break event
      // optimistically — refetch so the break reappears immediately instead
      // of only after a page reload.
      queryClient.invalidateQueries({
        queryKey: calendarKeys.workingHours.all(),
      });
    },
    meta: {
      successMessage: "Pause erfolgreich gelöscht.",
      errorMessage: "Fehler beim Löschen der Pause",
    },
  });
};

/**
 * Mutation to create working hours for a specific day with automatic lunch break generation
 * Creates a working hour exception with lunch breaks automatically added based on duration rules
 */
export const useCreateWorkingHoursForDay = () => {
  const { workingHours: api } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      userId, 
      date, 
      startTime, 
      endTime 
    }: { 
      userId: string; 
      date: string; 
      startTime: string; 
      endTime: string;
    }) => {
      return await api.createWorkingHoursForDay(userId, date, { startTime, endTime });
    },
    onSuccess: (data, variables) => {
      // console.log('[createWorkingHoursForDay] Success:', { data, variables });
      // Invalidate working hours to refresh the calendar view
      queryClient.invalidateQueries({
        queryKey: calendarKeys.workingHours.all(),
      });
      // console.log('[createWorkingHoursForDay] Queries invalidated');
    },
    meta: {
      successMessage: (data: any) => data.message || "Arbeitszeit erfolgreich hinzugefügt",
      errorMessage: "Fehler beim Hinzufügen der Arbeitszeit",
    },
  });
};

/**
 * Mutation to patch continuous timeslot times and user assignment
 * Replaces CalendarStore.patchContinuousTimeSlotTimesAndUser
 */
export const usePatchContinuousTimeSlotTimesAndUser = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      start,
      end,
      userId,
    }: {
      id: string;
      start: string;
      end: string;
      userId: string;
    }) => {
      // Central slot-level RESCHEDULE (R11): SPLITs the slot off, applies the new
      // window/user, reconciles a vacated continuous group anchor. Returns the
      // survivor continuous appointment.
      return await scheduler.schedulerRescheduleContinuousTimeSlot(id, { start, end, userId });
    },
    onMutate: async ({ id, start, end, userId }) => {
      // Provisional patch of the continuous slot's window/user inside whichever
      // cached continuous appointment contains it, so the cache agrees with
      // FullCalendar's optimistic placement during the in-flight window. Stamp
      // with a per-slot sequence so an out-of-order completion of a stale move
      // cannot clobber a newer one (last-issued-wins).
      const seq = beginAppointmentMutation(id);
      const snapshot: ContinuousCacheSnapshot = await optimisticContinuousTimeSlotPatch(
        queryClient,
        id,
        { start, end, userId }
      );
      return { snapshot, seq };
    },
    onError: (_error, variables, context) => {
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      if (context?.snapshot) {
        restoreContinuousCaches(queryClient, context.snapshot);
      }
    },
    onSuccess: (_data, variables, context) => {
      // Out-of-order guard: skip invalidations if a newer move of this same slot
      // has since been issued, so it does not snap back to this older position.
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      // Invalidate continuous appointments cache
      queryClient.invalidateQueries({
        queryKey: calendarKeys.continuousAppointmentsByDay.all(),
      });

      queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
      });
    },
    meta: {
      successMessage: "Dauertermin erfolgreich verschoben",
      errorMessage: "Fehler beim Verschieben des Dauertermins",
    },
  });
};

/**
 * The patient-scoped refetches a slot-level schedule/reschedule owes, mirroring
 * `useSaveAppointment.onSuccess`.
 *
 * The slot verbs used to invalidate `appointmentsByRx` and nothing else, so a
 * slot planned out of "Offene Termine" stayed on the shelf: neither the
 * unscheduled list nor the cancelled list (the shelf renders both) was told
 * anything, and both hold their data for 2 minutes. The canonical survivor is
 * already in the day cache via `reconcileSurvivorAppointment` — these
 * invalidations are only about the views the survivor DTO cannot speak for.
 */
const invalidateAfterSlotSchedule = (queryClient: QueryClient, survivor: any) => {
  const slots = (survivor?.timeSlots ?? []) as any[];

  // Sales slots carry the patient directly on the slot instead of via therapyRx.
  let patientId: any = slots
    .map((slot: any) => slot?.therapyRx?.rx?.patient?.id ?? slot?.patient?.id)
    .find(Boolean);
  if (typeof patientId === "string") patientId = parseInt(patientId);

  if (patientId) {
    queryClient.invalidateQueries({
      queryKey: appointmentKeys.appointmentsByPatient.detail(patientId),
    });
    queryClient.invalidateQueries({
      queryKey: calendarKeys.unscheduledPatientAppointments.detail(patientId),
    });
    // "Offene Termine" combines unscheduled AND cancelled appointments, so a
    // slot scheduled out of a cancellation would otherwise linger on the shelf.
    queryClient.invalidateQueries({
      queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(patientId),
    });
  } else {
    // Fall back to the whole scope rather than leaving the shelf stale — the
    // survivor always carries slots, so this only fires on a malformed DTO.
    queryClient.invalidateQueries({
      queryKey: calendarKeys.unscheduledPatientAppointments.all(),
    });
    queryClient.invalidateQueries({
      queryKey: appointmentKeys.cancelledAppointmentsByPatient.all(),
    });
  }

  const rxIds: string[] = [
    ...new Set(
      slots.map((slot: any) => slot?.therapyRx?.rx?.id).filter((rxId: any): rxId is string => !!rxId)
    ),
  ] as string[];
  for (const rxId of rxIds) {
    queryClient.invalidateQueries({ queryKey: appointmentKeys.appointmentsByRx.detail(rxId) });
  }
  return rxIds;
};

/**
 * Mutation to patch regular timeslot times and user assignment
 * Replaces CalendarStore.patchTimeSlotTimesAndUser
 */
export const usePatchTimeSlotTimesAndUser = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      start,
      end,
      userId,
    }: {
      id: string;
      start: string;
      end: string;
      userId: string;
      // Invoked synchronously right AFTER the optimistic patch in onMutate (same
      // semantics as useSaveAppointment.onOptimisticApplied): lets the caller
      // un-pin the placed "Ablage" slot in the same render cycle as the patch.
      onOptimisticApplied?: () => void;
    }) => {
      // Central slot-level RESCHEDULE (R11): SPLITs the slot off its appointment
      // (siblings untouched), applies the new window/user, resolves any prior
      // cancellation, reconciles a vacated group anchor, and MERGEs into the
      // patient's same-day appointment. Returns the survivor APPOINTMENT DTO
      // (not a TimeSlotDto).
      return await scheduler.schedulerRescheduleTimeSlot(id, { start, end, userId });
    },
    onMutate: async ({ id, start, end, userId, onOptimisticApplied }) => {
      // Provisional placement so the cache agrees with the calendar's optimistic
      // placement during the in-flight window: an in-place patch for a slot the
      // day cache already holds, a provisional insert for one lifted off the
      // shelf ("Ablage" slot pins go through this verb too).
      //
      // Stamped with a per-slot sequence so a SECOND move of the same slot
      // issued before this one settles cannot be clobbered by this (now stale)
      // request completing out of order — the same last-issued-wins guard the
      // appointment-level reschedule uses.
      const seq = beginAppointmentMutation(id);
      const snapshot = await optimisticSlotSchedule(queryClient, id, { start, end, userId });
      onOptimisticApplied?.();
      return { snapshot, seq };
    },
    onError: (_error, variables, context) => {
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      if (context?.snapshot) {
        restoreCalendarCaches(queryClient, context.snapshot.day);
        restoreShelfCaches(queryClient, context.snapshot.shelf);
      }
    },
    onSuccess: (data: any, variables, context) => {
      // Out-of-order guard: a newer move of this slot already owns the truth.
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      // Inject the canonical survivor appointment and de-duplicate its slots
      // from any other cached appointment (SPLIT/MERGE side effects, and the
      // provisional insert from onMutate) — no day refetch needed.
      if (data?.id) {
        reconcileSurvivorAppointment(queryClient, data);
      }

      // The scheduler verb returns the survivor appointment — derive the patient
      // and Rx ids from its slots (the old endpoint returned a TimeSlotDto).
      const rxIds = invalidateAfterSlotSchedule(queryClient, data);
      if (!rxIds.length) {
        notificationStore.showMessage(
          "UNABLE TO extract RX ID from scheduled appointment when trying to invalidate queries in mutation",
          "warning"
        );
        // console.debug("useUpdateTimeSlot timeSlots:", data);
      }
    },
    meta: {
      successMessage: "Termin erfolgreich verschoben",
      errorMessage: "Fehler beim Verschieben des Termins",
    },
  });
};

/**
 * Mutation to SCHEDULE a single UNSCHEDULED time slot from the shelf onto the
 * calendar. Distinct from `usePatchTimeSlotTimesAndUser` (which moves an already
 * planned slot): the source appointment is unscheduled and may carry a
 * cancellation that the backend ALWAYS resolves here. Server-side this:
 *   • deletes a supplement-only remainder (no split), OR splits the slot into
 *     its own appointment leaving the rest unscheduled,
 *   • resolves any cancellation on the source appointment,
 *   • recomputes, reconciles the group anchor, and MERGEs into the same-day
 *     appointment, returning the survivor APPOINTMENT DTO.
 */
export const useScheduleUnscheduledTimeSlot = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      start,
      end,
      userId,
    }: {
      id: string;
      start: string;
      end: string;
      userId: string;
      /**
       * Fully-hydrated source appointment for the optimistic insert. Optional:
       * when omitted the shelf query cache is consulted for it, which is where
       * every shelf drag's source lives anyway.
       */
      optimisticAppointment?: AppointmentDto;
      /** Invoked right AFTER the optimistic patch, in the same render cycle. */
      onOptimisticApplied?: () => void;
    }) => {
      return await scheduler.schedulerScheduleUnscheduledTimeSlot(id, { start, end, userId });
    },
    onMutate: async ({ id, start, end, userId, optimisticAppointment, onOptimisticApplied }) => {
      // The source appointment is UNSCHEDULED, so it is in the shelf query and
      // in no day cache: there is nothing to patch in place. `optimisticSlotSchedule`
      // therefore INSERTS a provisional appointment for the placed slot and drops
      // that slot from the shelf lists in the same pass, so the card and the grid
      // change together instead of the slot being on screen twice (or nowhere)
      // for the length of the roundtrip.
      const seq = beginAppointmentMutation(id);
      const snapshot = await optimisticSlotSchedule(
        queryClient,
        id,
        { start, end, userId },
        optimisticAppointment
      );
      onOptimisticApplied?.();
      return { snapshot, seq };
    },
    onError: (_error, variables, context) => {
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      if (context?.snapshot) {
        restoreCalendarCaches(queryClient, context.snapshot.day);
        restoreShelfCaches(queryClient, context.snapshot.shelf);
      }
    },
    onSuccess: (data: any, variables, context) => {
      if (context && !isLatestAppointmentMutation(variables.id, context.seq)) {
        return;
      }
      if (data?.id) {
        reconcileSurvivorAppointment(queryClient, data);
      }

      invalidateAfterSlotSchedule(queryClient, data);
    },
    meta: {
      successMessage: "Termin erfolgreich verschoben",
      errorMessage: "Fehler beim Verschieben des Termins",
    },
  });
};

export const useUserAbsence = () => {
  const { calendar: calendarApi } = useApi();
  const queryClient = useQueryClient();
   const locationId = useLocationId();
  return useMutation({
    mutationFn: async ({
      userId,
      leaveOfAbsence,
    }: {
      userId: string;
      leaveOfAbsence: LeaveOfAbsenceDto;
    }) => {
      return await calendarApi.userAbsence(userId, leaveOfAbsence);
    },
    onSuccess: async (_data, variables) => {
      if (locationId) {
        await queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointments.detail(locationId),
        });
      }
      await queryClient.invalidateQueries({
        queryKey: appointmentKeys.cancelledAppointmentsByPatient.all(),
      });
      await queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        });
    },
    meta: {
      successMessage: "Abwesenheit erfolgreich gespeichert",
      errorMessage: "Fehler beim Speichern der Abwesenheit",
    },
  });
};